import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  apiJson,
  createEngineApiClient,
  getEngineApiClients,
  getModels,
  getMyBillingUsage,
  getMyCapabilities,
  getApiErrorMessage,
  revokeEngineApiClient,
  updateModelAccountDescription,
} from '../api'
import { listConfigs } from '../api/graphConfigs'
import { copyAgencySafeConfig, getAgencySafeConfig, updateAgencySafeConfig } from '../api/graph_settings'
import ConditionBuilder, { ALL_CONDITION_PARAM_KEYS } from '../components/ConditionBuilder'
import AgencyMcpPanel from '../components/AgencyMcpPanel'
import AgencyTeamPanel from '../components/AgencyTeamPanel'
import TelegramServiceBotPanel from '../components/TelegramServiceBotPanel'
import VariableDefinitionsList from '../components/VariableDefinitionsList'
import TimezoneSelect from '../components/TimezoneSelect'
import { PageSection, Select, Switch } from '../components/ui'
import { useI18n } from '../i18n'
import { getStoredAuthRole, isAdminRole, ONBOARDING_ROLES } from '../lib/realm'
import { useToast } from '../contexts/ToastContext'
import { useWorkspaceScope } from '../contexts/WorkspaceScopeContext'
import { asList } from './v2/v2Utils'
import CustomPricingPreview, {
  CUSTOM_CAPABILITY_BOOLEAN_SETTING_KEYS,
  CUSTOM_CAPABILITY_CHECKLIST_SETTING_KEYS,
  CUSTOM_CAPABILITY_GROUPS,
  CUSTOM_CAPABILITY_LIST_SETTING_KEYS,
  CUSTOM_NUDITY_OPTIONS,
  CUSTOM_PRICE_SETTING_KEYS,
  CUSTOM_PROP_OPTIONS,
  withCustomPricingDefaults,
} from '../components/CustomPricingPreview'
import MonetizationRoutesSettings, {
  MONETIZATION_ROUTE_BOOLEAN_SETTING_KEYS,
  MONETIZATION_ROUTE_STRING_SETTING_KEYS,
  withMonetizationRouteDefaults,
} from '../components/MonetizationRoutesSettings'
import '../styles/pages/settings.css'
import '../styles/pages/page-common.css'

const DEFAULT_INTERVAL_MINUTES = 24 * 60
const INTERVAL_STEP_MINUTES = 10
const MAX_INTERVAL_MINUTES = 30 * 24 * 60

const BOOLEAN_SETTING_KEYS = new Set([
  'proactive_skip_test_fans',
  'proactive_webhook_users_online_enabled',
  'proactive_webhook_users_typing_enabled',
  'proactive_webhook_posts_liked_enabled',
  'proactive_webhook_posts_commented_enabled',
  'history_reactivation_enabled',
  'history_reactivation_dry_run',
  'history_reactivation_include_active_free',
  'history_reactivation_include_free_with_history',
  'history_reactivation_free_auto_approve',
  'history_reactivation_auto_dispatch_enabled',
  'prompt_language_match_fan',
  'custom_requests_enabled',
  'custom_pricing_enabled',
  ...CUSTOM_CAPABILITY_BOOLEAN_SETTING_KEYS,
  'timewaster_strategy_enabled',
  ...MONETIZATION_ROUTE_BOOLEAN_SETTING_KEYS,
])

const LIST_SETTING_KEYS = new Set([
  'proactive_webhook_custom_events',
  'prompt_allowed_languages',
  'prompt_disallowed_topics',
  ...CUSTOM_CAPABILITY_LIST_SETTING_KEYS,
])
const CHECKLIST_SETTING_KEYS = new Set(CUSTOM_CAPABILITY_CHECKLIST_SETTING_KEYS)

const PROACTIVE_SETTING_KEYS = [
  'proactive_message_generator_conversation_messages_count',
  'proactive_followup_max_dispatch_per_tick',
  'proactive_skip_test_fans',
  'proactive_cannot_send_retry_minutes',
  'proactive_daily_touch_max_hours',
  'proactive_recent_continuation_max_days',
  'proactive_warm_reactivation_max_days',
]

const WEBHOOK_SETTING_KEYS = [
  'proactive_webhook_min_silence_minutes',
  'proactive_webhook_cooldown_minutes',
]

const HISTORY_SETTING_KEYS = [
  'history_reactivation_enabled',
  'history_reactivation_dry_run',
  'history_reactivation_batch_size',
  'history_reactivation_scan_max_pages',
  'history_reactivation_include_active_free',
  'history_reactivation_include_free_with_history',
  'history_reactivation_free_auto_approve',
  'history_reactivation_auto_dispatch_enabled',
  'history_reactivation_auto_dispatch_limit',
  'history_reactivation_messages_limit',
  'history_reactivation_min_silence_hours',
  'history_reactivation_max_age_days',
  'history_reactivation_dispatch_limit',
  'history_reactivation_max_attempts',
  'history_reactivation_retry_minutes',
]

const PROMPT_CONTROL_KEYS = [
  'prompt_language_match_fan',
  'prompt_primary_language',
  'prompt_allowed_languages',
  'prompt_tone_style',
  'prompt_emoji_level',
  'prompt_message_length',
  'prompt_disallowed_topics',
  'prompt_custom_safe_instructions',
]

const TIMEWASTER_SETTING_KEYS = [
  'timewaster_strategy_enabled',
  'timewaster_min_fan_messages',
  'timewaster_high_tension_min',
  'timewaster_low_spend_intent_max',
  'timewaster_reply_delay_min_seconds',
  'timewaster_reply_delay_max_seconds',
]

const QUEUE_STATUS_OPTIONS = [
  '',
  'pending_sync',
  'synced',
  'candidate',
  'approved',
  'dispatching',
  'sent',
  'skipped',
  'failed',
  'no_history',
]

const AUTOMATION_MODE_OPTIONS = ['full_auto', 'handoff_on_offer', 'handoff_after_first_purchase']

const TELEGRAM_ALERT_EVENTS = [
  'purchase',
  'state_changed',
  'low_balance',
  'handoff_on_offer',
  'handoff_after_first_purchase',
  'insufficient_credit',
  'graph_failed',
  'openai_timeout',
]

const AGENCY_SETTING_SECTIONS = ['models', 'onboarding', 'l4', 'proactive', 'timewasters', 'customPricing', 'monetizationRoutes', 'prompt', 'reactivation', 'transitions', 'mcp']
const CONDITIONAL_AGENT_KEYS = ['message_generator', 'photo_generator', 'offer_generator', 'proactive_message_generator']
const AGENCY_SECTION_ALIASES = {
  history: 'reactivation',
  reactivationQueue: 'reactivation',
  reactivation_queue: 'reactivation',
  conditional: 'conditionalPrompts',
  conditional_prompts: 'conditionalPrompts',
  conditionalPrompts: 'conditionalPrompts',
}

const KNOWN_TRANSITION_LABEL_KEYS = {
  run_monetization: 'settings.transition.salesBranch',
  emit_chat_media: 'settings.transition.emitChatMedia',
  postbuy_block: 'settings.transition.postbuyBlock',
  advance_script_on_free_send: 'settings.transition.advanceScriptOnFreeSend',
}

const KNOWN_TRANSITION_HINT_KEYS = {
  run_monetization: 'settings.transition.salesBranchHint',
  emit_chat_media: 'settings.transition.emitChatMediaHint',
  postbuy_block: 'settings.transition.postbuyBlockHint',
  advance_script_on_free_send: 'settings.transition.advanceScriptOnFreeSendHint',
}

function toJson(value) {
  return JSON.stringify(value ?? null, null, 2)
}

function parseJson(text, fallback) {
  const trimmed = String(text || '').trim()
  if (!trimmed) return fallback
  return JSON.parse(trimmed)
}

function readBool(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return !['', '0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  return Boolean(value)
}

function initModelDraft(model = {}) {
  const events = Array.isArray(model.telegram_alert_events)
    ? model.telegram_alert_events.filter((eventName) => TELEGRAM_ALERT_EVENTS.includes(eventName))
    : [...TELEGRAM_ALERT_EVENTS]
  return {
    description: model.account_description || '',
    timezone: model.timezone || '',
    automation_mode: AUTOMATION_MODE_OPTIONS.includes(model.automation_mode) ? model.automation_mode : 'full_auto',
    bot_paused: readBool(model.bot_paused),
    default_config_id: model.default_config_id == null ? '' : String(model.default_config_id),
    telegram_alerts_enabled: readBool(model.telegram_alerts_enabled),
    telegram_chat_id: model.telegram_chat_id || '',
    telegram_bot_token: '',
    telegram_alert_events: events,
  }
}

function toCsv(value) {
  if (Array.isArray(value)) return value.join(', ')
  return String(value || '')
}

function csvToList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean)
  return String(value || '').split(',').map((v) => v.trim()).filter(Boolean)
}

function normalizeSettingsPayload(settings) {
  const out = {}
  for (const [key, value] of Object.entries(settings || {})) {
    if (BOOLEAN_SETTING_KEYS.has(key)) {
      out[key] = readBool(value)
    } else if (CHECKLIST_SETTING_KEYS.has(key)) {
      out[key] = Array.isArray(value) ? value : []
    } else if (LIST_SETTING_KEYS.has(key)) {
      out[key] = csvToList(value)
    } else if (key === 'prompt_custom_safe_instructions' || key === 'prompt_tone_style' || key === 'prompt_primary_language' || MONETIZATION_ROUTE_STRING_SETTING_KEYS.has(key)) {
      out[key] = String(value ?? '').trim()
    } else if (key === 'prompt_emoji_level' || key === 'prompt_message_length') {
      out[key] = String(value ?? '').trim() || 'auto'
    } else {
      const n = Number(value)
      out[key] = Number.isFinite(n) ? n : value
    }
  }
  return out
}

function readRuleMinutes(rule) {
  if (rule?.minutes != null) return Number(rule.minutes)
  if (rule?.interval_minutes != null) return Number(rule.interval_minutes)
  if (rule?.hours != null) return Number(rule.hours) * 60
  return DEFAULT_INTERVAL_MINUTES
}

function normalizeIntervalMinutes(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return DEFAULT_INTERVAL_MINUTES
  const rounded = Math.round(n / INTERVAL_STEP_MINUTES) * INTERVAL_STEP_MINUTES
  return Math.max(INTERVAL_STEP_MINUTES, Math.min(MAX_INTERVAL_MINUTES, rounded))
}

function emptyCondition() {
  return { param: ALL_CONDITION_PARAM_KEYS[0] || 'engagement_score', op: 'gte', value: 0 }
}

function emptyRule() {
  return { label: '', conditions: emptyCondition(), minutes: DEFAULT_INTERVAL_MINUTES, prompt: '' }
}

function emptyConditionalPromptRule(order = 0) {
  return { condition: emptyCondition(), prompt_text: '', role: 'system', order, enabled: true }
}

function normalizeConditionalPromptRule(rule, index = 0) {
  return {
    ...emptyConditionalPromptRule(index),
    ...rule,
    condition: rule?.condition || emptyCondition(),
    prompt_text: String(rule?.prompt_text || ''),
    role: ['system', 'user'].includes(rule?.role) ? rule.role : 'system',
    order: Number.isFinite(Number(rule?.order)) ? Number(rule.order) : index,
    enabled: rule?.enabled !== false,
  }
}

function normalizeConditionalPrompts(value) {
  const out = {}
  for (const agentKey of CONDITIONAL_AGENT_KEYS) {
    const rules = Array.isArray(value?.[agentKey]) ? value[agentKey] : []
    out[agentKey] = rules.map(normalizeConditionalPromptRule)
  }
  return out
}

function conditionalPromptsPayload(value) {
  const out = {}
  for (const agentKey of CONDITIONAL_AGENT_KEYS) {
    out[agentKey] = (value?.[agentKey] || []).map((rule, index) => ({
      condition: rule.condition || emptyCondition(),
      prompt_text: String(rule.prompt_text || '').trim(),
      role: ['system', 'user'].includes(rule.role) ? rule.role : 'system',
      order: Number.isFinite(Number(rule.order)) ? Number(rule.order) : index,
      enabled: rule.enabled !== false,
    }))
  }
  return out
}

function normalizeRule(rule) {
  return {
    ...emptyRule(),
    ...rule,
    minutes: normalizeIntervalMinutes(readRuleMinutes(rule)),
    conditions: rule?.conditions || emptyCondition(),
  }
}

function toRulePayload(rule) {
  const payload = {
    conditions: rule?.conditions || emptyCondition(),
    minutes: normalizeIntervalMinutes(readRuleMinutes(rule)),
  }
  const label = String(rule?.label || '').trim()
  const trigger = String(rule?.trigger || '').trim()
  const prompt = String(rule?.prompt || rule?.prompt_text || '').trim()
  if (label) payload.label = label
  if (trigger) payload.trigger = trigger
  if (prompt) payload.prompt = prompt
  return payload
}

function createTransitionRow(key, condition, index = 0) {
  return {
    id: `${key || 'transition'}-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    key: String(key || '').trim(),
    condition: condition || emptyCondition(),
  }
}

function transitionRowsFromObject(transitions) {
  return Object.entries(transitions || {}).map(([key, condition], index) => createTransitionRow(key, condition, index))
}

function transitionRowsToObject(rows) {
  const out = {}
  for (const row of rows || []) {
    const key = String(row?.key || '').trim()
    if (!key) continue
    out[key] = row.condition || emptyCondition()
  }
  return out
}

function uniqueTransitionKey(rows) {
  const used = new Set((rows || []).map((row) => String(row?.key || '').trim()).filter(Boolean))
  let idx = 1
  let key = 'new_transition'
  while (used.has(key)) {
    idx += 1
    key = `new_transition_${idx}`
  }
  return key
}

function transitionRowsError(rows, t) {
  const seen = new Set()
  for (const row of rows || []) {
    const key = String(row?.key || '').trim()
    if (!key) return t('agencySettings.transitions.emptyKey')
    if (seen.has(key)) return t('agencySettings.transitions.duplicateKey', { key })
    seen.add(key)
  }
  return ''
}

function transitionLabel(t, key) {
  return KNOWN_TRANSITION_LABEL_KEYS[key] ? t(KNOWN_TRANSITION_LABEL_KEYS[key]) : key
}

function transitionHint(t, key) {
  return KNOWN_TRANSITION_HINT_KEYS[key] ? t(KNOWN_TRANSITION_HINT_KEYS[key]) : ''
}

function providerDisplayName(provider) {
  const key = String(provider || '').toLowerCase()
  if (key === 'onlyfansapi' || key === 'onlyfans') return 'OnlyFans'
  if (key === 'fanltv_ofapi') return 'FanLTV OFAPI Backup'
  if (key === 'fanvue') return 'Fanvue'
  if (key === 'telegram' || key === 'telegram_account') return 'Telegram'
  if (key === 'aigirlfactory' || key === 'aigf') return 'AIGirlFactory'
  return provider || 'Provider'
}

function connectionStatus(model) {
  const links = Array.isArray(model.provider_links) ? model.provider_links : []
  const preferred = links.find((link) => ['connected', 'active'].includes(String(link?.connection_status || link?.status || '').toLowerCase()))
    || links.find((link) => link?.connection_status || link?.status)
    || links[0]
  if (!preferred) return 'pending'
  const status = preferred.connection_status || preferred.status || 'pending'
  return `${providerDisplayName(preferred.provider)}: ${status}`
}

function automationLabel(t, value) {
  const labels = {
    full_auto: t('modelDetail.account.automation.fullAuto'),
    handoff_on_offer: t('modelDetail.account.automation.handoffOnOffer'),
    handoff_after_first_purchase: t('modelDetail.account.automation.handoffAfterFirstPurchase'),
  }
  return labels[value] || labels.full_auto
}

function configDisplayName(configs, configId, t) {
  if (configId == null || configId === '') return t('agencySettings.models.defaultConfigActive')
  const match = configs.find((config) => String(config.id) === String(configId))
  return match?.name || `Config #${configId}`
}

function formatCents(cents, currency) {
  const value = Number(cents || 0) / 100
  return `${value.toFixed(2)} ${currency || 'USD'}`
}

function historyQueue(modelId, status) {
  const params = new URLSearchParams({ model_id: modelId, limit: 20, offset: 0 })
  if (status) params.set('status', status)
  return apiJson(`/bot/history-reactivation/queue?${params}`, {}, 'Failed to load reactivation queue')
}

function historyTick(modelId) {
  return apiJson(`/bot/history-reactivation/tick?model_id=${encodeURIComponent(modelId)}`, { method: 'POST' }, 'Failed to queue reactivation tick')
}

function historyDispatch(modelId) {
  return apiJson(`/bot/history-reactivation/dispatch?model_id=${encodeURIComponent(modelId)}`, { method: 'POST' }, 'Failed to queue reactivation dispatch')
}

function historyApprove(modelId, queueId) {
  return apiJson(`/bot/history-reactivation/queue/${queueId}/approve?model_id=${encodeURIComponent(modelId)}`, { method: 'POST' }, 'Failed to approve candidate')
}

function historySkip(modelId, queueId) {
  return apiJson(
    `/bot/history-reactivation/queue/${queueId}/skip?model_id=${encodeURIComponent(modelId)}`,
    { method: 'POST', body: JSON.stringify({ reason: 'agency_skip' }) },
    'Failed to skip candidate',
  )
}

function normalizeAgencySection(section) {
  const raw = String(section || '').trim()
  return AGENCY_SECTION_ALIASES[raw] || raw
}

function readAgencySettingsSection(location) {
  const search = new URLSearchParams(location?.search || '')
  const fromQuery = search.get('section')
  const fromHash = String(location?.hash || '').replace(/^#/, '')
  const section = normalizeAgencySection(fromQuery || fromHash)
  return [...AGENCY_SETTING_SECTIONS, 'conditionalPrompts', 'engineApi'].includes(section) ? section : ''
}

export default function AgencySettingsPage() {
  const { t } = useI18n()
  const { addToast } = useToast()
  const {
    selectedModelId: workspaceModelId,
    selectedModel: workspaceModel,
    setSelectedModelId: setWorkspaceModelId,
  } = useWorkspaceScope()
  const location = useLocation()
  const requestedSection = useMemo(
    () => readAgencySettingsSection(location),
    [location.search, location.hash],
  )
  const variableEditorRef = useRef(null)
  const [models, setModels] = useState([])
  const [modelDrafts, setModelDrafts] = useState({})
  const [configs, setConfigs] = useState([])
  const [billing, setBilling] = useState(null)
  const [selectedConfigId, setSelectedConfigId] = useState('')
  const [safeConfig, setSafeConfig] = useState(null)
  const [canViewConditionalPrompts, setCanViewConditionalPrompts] = useState(false)
  const [engineApiEnabled, setEngineApiEnabled] = useState(false)
  const [engineClients, setEngineClients] = useState([])
  const [newEngineCredentials, setNewEngineCredentials] = useState(null)
  const [variableDefinitions, setVariableDefinitions] = useState([])
  const [transitionsDraft, setTransitionsDraft] = useState([])
  const [proactiveRules, setProactiveRules] = useState([])
  const [conditionalPrompts, setConditionalPrompts] = useState(() => normalizeConditionalPrompts({}))
  const [settingsDraft, setSettingsDraft] = useState({})
  const [selectedModelId, setSelectedModelId] = useState('')
  const [queueStatus, setQueueStatus] = useState('candidate')
  const [queueRows, setQueueRows] = useState([])
  const [queueTotal, setQueueTotal] = useState(0)
  const [queueLoading, setQueueLoading] = useState(false)
  const [activeSection, setActiveSection] = useState(() => requestedSection || 'models')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copyingConfig, setCopyingConfig] = useState(false)
  const [modelSavingId, setModelSavingId] = useState(null)
  const [error, setError] = useState('')
  const [warnings, setWarnings] = useState([])
  const configLoadSequenceRef = useRef(0)
  const lastWorkspaceSyncRef = useRef('')
  const currentRole = getStoredAuthRole()
  const canManageModelDefaultConfig = isAdminRole(currentRole) || currentRole === 'tester_plus'

  async function loadPage(configId = selectedConfigId) {
    setLoading(true)
    setError('')
    try {
      const capabilitiesResponse = await getMyCapabilities()
      const canUseEngineApi = Boolean(capabilitiesResponse?.engine_api_enabled)
      const [modelsResponse, configResponse, billingResponse, configsResponse, engineResponse] = await Promise.all([
        getModels(),
        getAgencySafeConfig(configId || null),
        getMyBillingUsage(),
        canManageModelDefaultConfig ? listConfigs() : Promise.resolve([]),
        canUseEngineApi ? getEngineApiClients() : Promise.resolve({ data: [] }),
      ])
      const modelList = Array.isArray(modelsResponse) ? modelsResponse : modelsResponse?.data || []
      setModels(modelList)
      setModelDrafts(Object.fromEntries(modelList.map((model) => [model.id, initModelDraft(model)])))
      const scopedModelExists = modelList.some((model) => String(model.id) === String(workspaceModelId))
      setSelectedModelId(scopedModelExists ? String(workspaceModelId) : '')
      setBilling(billingResponse)
      setEngineApiEnabled(canUseEngineApi)
      setEngineClients(Array.isArray(engineResponse?.data) ? engineResponse.data : [])
      setConfigs(asList(configsResponse))
      applyConfigResponse(configResponse, configId)
    } catch (err) {
      setError(getApiErrorMessage(err, t('common.error')))
    } finally {
      setLoading(false)
    }
  }

  function applyConfigResponse(configResponse, fallbackConfigId = '') {
    setSafeConfig(configResponse)
    setSelectedConfigId(String(configResponse?.id || fallbackConfigId || ''))
    setVariableDefinitions(configResponse?.variable_definitions || [])
    setTransitionsDraft(transitionRowsFromObject(configResponse?.graph_transitions || {}))
    setProactiveRules((configResponse?.proactive_followup_rules || []).map(normalizeRule))
    setCanViewConditionalPrompts(Boolean(configResponse?.agency_can_view_conditional_prompts))
    setConditionalPrompts(normalizeConditionalPrompts(configResponse?.conditional_prompts || {}))
    const nextSettings = withMonetizationRouteDefaults(withCustomPricingDefaults(configResponse?.settings || {}))
    for (const key of LIST_SETTING_KEYS) {
      if (key in nextSettings) nextSettings[key] = toCsv(nextSettings[key])
    }
    setSettingsDraft(nextSettings)
  }

  async function loadCustomConfigForModel(modelId) {
    const model = models.find((item) => String(item.id) === String(modelId))
    const activeConfig = configs.find((config) => config.is_active)
    const configId = model?.default_config_id ?? activeConfig?.id ?? null
    const requestSequence = ++configLoadSequenceRef.current
    setLoading(true)
    setError('')
    try {
      const response = await getAgencySafeConfig(configId)
      if (requestSequence !== configLoadSequenceRef.current) return
      applyConfigResponse(response, configId == null ? '' : String(configId))
    } catch (err) {
      if (requestSequence === configLoadSequenceRef.current) {
        setError(getApiErrorMessage(err, t('common.error')))
      }
    } finally {
      if (requestSequence === configLoadSequenceRef.current) setLoading(false)
    }
  }

  async function handleCustomModelChange(modelId) {
    const nextModelId = String(modelId || '')
    const model = models.find((item) => String(item.id) === nextModelId)
    const activeConfig = configs.find((config) => config.is_active)
    const configId = String(model?.default_config_id ?? activeConfig?.id ?? '')
    setSelectedModelId(nextModelId)
    setWorkspaceModelId(nextModelId)
    lastWorkspaceSyncRef.current = `${nextModelId}:${configId}`
    await loadCustomConfigForModel(modelId)
  }

  useEffect(() => {
    loadPage(workspaceModel?.default_config_id ?? '')
  }, [])

  useEffect(() => {
    if (selectedModelId) refreshQueue(selectedModelId, queueStatus)
  }, [selectedModelId, queueStatus])

  const modelOptions = useMemo(
    () => models.map((model) => ({ value: String(model.id), label: model.of_email || model.name || `#${model.id}` })),
    [models],
  )
  const graphConfigOptions = useMemo(
    () => [
      { value: '', label: t('agencySettings.models.defaultConfigActive') },
      ...configs.map((config) => ({
        value: String(config.id),
        label: `${config.name || `Config #${config.id}`}${config.is_active ? ` (${t('agencySettings.models.defaultConfigActiveBadge')})` : ''}`,
      })),
    ],
    [configs, t],
  )
  const activeGraphConfig = configs.find((config) => config.is_active)
  const selectedCustomModel = models.find((model) => String(model.id) === String(selectedModelId))
  const selectedModelConfigId = String(
    selectedCustomModel?.default_config_id ?? activeGraphConfig?.id ?? selectedConfigId ?? '',
  )
  const selectedModelConfig = configs.find((config) => String(config.id) === selectedModelConfigId)
  const customConfigModels = models.filter((model) => String(
    model.default_config_id ?? activeGraphConfig?.id ?? '',
  ) === selectedModelConfigId)

  const visibleSections = useMemo(
    () => {
      const baseSections = AGENCY_SETTING_SECTIONS.filter((section) => section !== 'onboarding' || ONBOARDING_ROLES.includes(currentRole))
      const sections = canViewConditionalPrompts ? [...baseSections, 'conditionalPrompts'] : [...baseSections]
      sections.push('telegram')
      if (currentRole === 'agency') sections.push('team')
      if (engineApiEnabled) sections.push('engineApi')
      return sections
    },
    [canViewConditionalPrompts, engineApiEnabled, currentRole],
  )

  useEffect(() => {
    if (!canViewConditionalPrompts && activeSection === 'conditionalPrompts') {
      setActiveSection('prompt')
    }
  }, [activeSection, canViewConditionalPrompts])

  useEffect(() => {
    if (requestedSection && visibleSections.includes(requestedSection)) {
      setActiveSection(requestedSection)
    }
  }, [requestedSection, visibleSections])

  useEffect(() => {
    if (loading) return
    const nextModelId = String(workspaceModelId || '')
    const model = models.find((item) => String(item.id) === nextModelId)
    const activeConfig = configs.find((config) => config.is_active)
    const configId = String(model?.default_config_id ?? activeConfig?.id ?? '')
    const syncKey = `${nextModelId}:${configId}`
    if (lastWorkspaceSyncRef.current === syncKey) return
    lastWorkspaceSyncRef.current = syncKey
    setSelectedModelId(nextModelId)
    loadCustomConfigForModel(nextModelId)
  }, [workspaceModelId, models, configs, loading])

  const l4Keys = variableDefinitions.map((d) => d.key).filter(Boolean)
  const conditionParamKeys = [...new Set([...ALL_CONDITION_PARAM_KEYS, ...l4Keys])]

  async function saveVariableDefinitions(next) {
    const result = await updateAgencySafeConfig({ variable_definitions: next }, selectedConfigId || null)
    setVariableDefinitions(next)
    setSafeConfig(result.config)
    setWarnings(result.warnings || [])
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setWarnings([])
    try {
      const transitionError = transitionRowsError(transitionsDraft, t)
      if (transitionError) {
        setError(transitionError)
        return
      }
      await variableEditorRef.current?.save()
      const payload = {
        graph_transitions: transitionRowsToObject(transitionsDraft),
        proactive_followup_rules: proactiveRules.map(toRulePayload),
        settings: normalizeSettingsPayload(settingsDraft),
      }
      if (canViewConditionalPrompts) {
        payload.conditional_prompts = conditionalPromptsPayload(conditionalPrompts)
      }
      const result = await updateAgencySafeConfig(payload, selectedConfigId || null)
      applyConfigResponse(result.config, selectedConfigId)
      setWarnings(result.warnings || [])
      addToast(t('agencySettings.config.saved'), 'success')
    } catch (err) {
      setError(getApiErrorMessage(err, t('agencySettings.config.saveError')))
    } finally {
      setSaving(false)
    }
  }

  async function handleCopyAgencyConfig() {
    setCopyingConfig(true)
    setError('')
    try {
      const result = await copyAgencySafeConfig({ assign_to_models: true })
      applyConfigResponse(result.config, '')
      addToast(t('agencySettings.config.copied'), 'success')
    } catch (err) {
      setError(getApiErrorMessage(err, t('agencySettings.config.copyError')))
    } finally {
      setCopyingConfig(false)
    }
  }

  function updateSetting(key, value) {
    setSettingsDraft((prev) => ({ ...prev, [key]: value }))
  }

  function updateModelDraft(modelId, updates) {
    setModelDrafts((prev) => {
      const model = models.find((item) => item.id === modelId) || {}
      return {
        ...prev,
        [modelId]: {
          ...(prev[modelId] || initModelDraft(model)),
          ...updates,
        },
      }
    })
  }

  function toggleTelegramEvent(modelId, eventName) {
    setModelDrafts((prev) => {
      const model = models.find((item) => item.id === modelId) || {}
      const draft = prev[modelId] || initModelDraft(model)
      const current = new Set(draft.telegram_alert_events || [])
      if (current.has(eventName)) current.delete(eventName)
      else current.add(eventName)
      return {
        ...prev,
        [modelId]: {
          ...draft,
          telegram_alert_events: TELEGRAM_ALERT_EVENTS.filter((item) => current.has(item)),
        },
      }
    })
  }

  async function saveModelSettings(model, overrides = {}) {
    const draft = {
      ...(modelDrafts[model.id] || initModelDraft(model)),
      ...overrides,
    }
    const defaultConfigId = draft.default_config_id === '' ? null : Number(draft.default_config_id)
    const payload = {
      description: String(draft.description || '').trim(),
      timezone: String(draft.timezone || '').trim(),
      max_message_price: model.max_message_price,
      automation_mode: draft.automation_mode || 'full_auto',
      bot_paused: readBool(draft.bot_paused),
    }
    if (canManageModelDefaultConfig) {
      payload.default_config_id = Number.isFinite(defaultConfigId) ? defaultConfigId : null
    }

    setModelSavingId(model.id)
    setError('')
    try {
      const updated = await updateModelAccountDescription(model.id, payload)
      setModels((prev) => prev.map((item) => (item.id === model.id ? { ...item, ...updated } : item)))
      setModelDrafts((prev) => ({
        ...prev,
        [model.id]: initModelDraft({ ...model, ...updated }),
      }))
      addToast(t('agencySettings.models.saved'), 'success')
    } catch (err) {
      setError(getApiErrorMessage(err, t('agencySettings.models.saveError')))
    } finally {
      setModelSavingId(null)
    }
  }

  function updateRule(idx, updates) {
    setProactiveRules((prev) => {
      const next = [...prev]
      next[idx] = { ...(next[idx] || emptyRule()), ...updates }
      return next
    })
  }

  function addTransitionRow() {
    setTransitionsDraft((prev) => [...prev, createTransitionRow(uniqueTransitionKey(prev), emptyCondition(), prev.length)])
  }

  function updateTransitionRow(rowId, updates) {
    setTransitionsDraft((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...updates } : row)))
  }

  function removeTransitionRow(rowId) {
    setTransitionsDraft((prev) => prev.filter((row) => row.id !== rowId))
  }

  async function refreshQueue(modelId = selectedModelId, status = queueStatus) {
    if (!modelId) return
    setQueueLoading(true)
    try {
      const res = await historyQueue(modelId, status)
      setQueueRows(Array.isArray(res?.data) ? res.data : [])
      setQueueTotal(res?.total ?? 0)
    } catch (err) {
      addToast(getApiErrorMessage(err, t('common.error')), 'error')
    } finally {
      setQueueLoading(false)
    }
  }

  async function runHistoryAction(action) {
    if (!selectedModelId) return
    setQueueLoading(true)
    try {
      await action(selectedModelId)
      addToast(t('agencySettings.reactivation.queued'), 'success')
      await refreshQueue(selectedModelId, queueStatus)
    } catch (err) {
      addToast(getApiErrorMessage(err, t('common.error')), 'error')
    } finally {
      setQueueLoading(false)
    }
  }

  async function updateQueueItem(queueId, action) {
    if (!selectedModelId) return
    setQueueLoading(true)
    try {
      await action(selectedModelId, queueId)
      await refreshQueue(selectedModelId, queueStatus)
    } catch (err) {
      addToast(getApiErrorMessage(err, t('common.error')), 'error')
    } finally {
      setQueueLoading(false)
    }
  }

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>

  return (
    <PageSection
      className="agency-settings-page"
      title={t('agencySettings.title')}
      hint={t('agencySettings.hint')}
    >
      {error && <div className="settings-error">{error}</div>}

      {billing && (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('agencySettings.billing.plan')}</th>
                <th>{t('agencySettings.billing.balance')}</th>
                <th>{t('agencySettings.billing.lowBalanceThreshold')}</th>
                <th>{t('agencySettings.billing.rate')}</th>
                <th>{t('agencySettings.billing.sent30d')}</th>
                <th>{t('agencySettings.billing.messages30d')}</th>
                <th>{t('agencySettings.billing.amount30d')}</th>
                <th>{t('agencySettings.billing.available')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t(`users.billing.plan.${billing.plan || 'monthly_flat'}`)}</td>
                <td>{formatCents(billing.balance_cents, billing.currency)}</td>
                <td>{billing.low_balance_alert_threshold_cents != null ? formatCents(billing.low_balance_alert_threshold_cents, billing.currency) : t('common.notSelected')}</td>
                <td>{formatCents(billing.rate_cents, billing.currency)}</td>
                <td>{billing.outgoing_bot_messages_30d ?? 0}</td>
                <td>{billing.billable_bot_messages_30d ?? 0}</td>
                <td>{formatCents(billing.charged_amount_30d_cents ?? billing.estimated_amount_30d_cents, billing.currency)}</td>
                <td>{billing.available_messages == null ? t('common.notSelected') : billing.available_messages}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('agencySettings.models.model')}</th>
              <th>{t('agencySettings.models.connection')}</th>
              <th>{t('agencySettings.models.automation')}</th>
              <th>{t('agencySettings.models.botStatus')}</th>
              <th>{t('agencySettings.models.telegram')}</th>
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.id}>
                <td>{model.of_email || model.name || `#${model.id}`}</td>
                <td>{connectionStatus(model)}</td>
                <td>{automationLabel(t, model.automation_mode || 'full_auto')}</td>
                <td>{readBool(model.bot_paused) ? t('agencySettings.models.botPaused') : t('agencySettings.models.botActive')}</td>
                <td>{model.telegram_alerts_enabled ? t('agencySettings.models.enabled') : t('agencySettings.models.disabled')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="agency-settings-tabs" role="tablist" aria-label={t('agencySettings.tabs.label')}>
        {visibleSections.map((section) => (
          <button
            key={section}
            type="button"
            role="tab"
            aria-selected={activeSection === section}
            className={`agency-settings-tab${activeSection === section ? ' active' : ''}`}
            onClick={() => setActiveSection(section)}
          >
            {section === 'team' ? 'Team' : section === 'telegram' ? 'Telegram service bot' : t(`agencySettings.tabs.${section}`)}
          </button>
        ))}
      </div>

      {activeSection === 'onboarding' && ONBOARDING_ROLES.includes(currentRole) ? (
        <ConfigBlock title={t('agencySettings.onboarding.title')} hint={t('agencySettings.onboarding.hint')}>
          <p>{t('agencySettings.onboarding.safety')}</p>
          <Link className="v2-btn v2-btn-primary" to="/v2/onboarding">Add a new model / start onboarding</Link>
          <div className="agency-onboarding-links">
            {models.map((model) => (
              <div className="agency-onboarding-link-row" key={model.id}>
                <strong>{model.name || model.of_email || `#${model.id}`}</strong>
                <Link className="v2-btn v2-btn-primary" to={`/v2/onboarding?model_id=${model.id}`}>{t('agencySettings.onboarding.open')}</Link>
              </div>
            ))}
            {!models.length ? <p>{t('agencySettings.models.empty')}</p> : null}
          </div>
        </ConfigBlock>
      ) : activeSection === 'team' && currentRole === 'agency' ? <AgencyTeamPanel models={models} /> : activeSection === 'telegram' ? <TelegramServiceBotPanel models={models} /> : activeSection === 'mcp' ? <AgencyMcpPanel models={models} /> : activeSection === 'engineApi' ? (
        <EngineApiPanel
          t={t}
          models={models}
          clients={engineClients}
          credentials={newEngineCredentials}
          onCreated={(client) => {
            setNewEngineCredentials(client)
            setEngineClients((prev) => [client, ...prev])
          }}
          onRevoked={(clientId) => setEngineClients((prev) => prev.map((client) => (
            client.id === clientId ? { ...client, is_active: false } : client
          )))}
          createClient={createEngineApiClient}
          revokeClient={revokeEngineApiClient}
          addToast={addToast}
        />
      ) : activeSection === 'models' ? (
        <ConfigBlock title={t('agencySettings.models.settingsTitle')} hint={t('agencySettings.models.settingsHint')}>
          <div className="agency-model-settings-list">
            {models.map((model) => {
              const draft = modelDrafts[model.id] || initModelDraft(model)
              const profileReady = Boolean(String(draft.description || '').trim() && String(draft.timezone || '').trim())
              return (
                <section className="agency-model-settings-item" key={model.id}>
                  <div className="agency-model-settings-header">
                    <div>
                      <h4>{model.of_email || model.name || `#${model.id}`}</h4>
                      <p className="muted">{t('agencySettings.models.connection')}: {connectionStatus(model)}</p>
                    </div>
                    <div className="agency-model-summary-stack">
                      <span className="agency-model-mode-summary">{automationLabel(t, draft.automation_mode)}</span>
                      <span className="agency-model-mode-summary">{configDisplayName(configs, draft.default_config_id, t)}</span>
                      <span className={`agency-model-bot-status${readBool(draft.bot_paused) ? ' paused' : ''}`}>
                        {readBool(draft.bot_paused) ? t('agencySettings.models.botPaused') : t('agencySettings.models.botActive')}
                      </span>
                      <span className={`agency-model-readiness${profileReady ? ' ready' : ''}`}>
                        {profileReady ? t('agencySettings.models.profileReady') : t('agencySettings.models.profileMissing')}
                      </span>
                    </div>
                  </div>

                  <div className="settings-grid agency-model-settings-grid">
                    <div className="setting-row agency-model-description-row">
                      <div className="setting-label-wrap">
                        <label className="setting-key">{t('agencySettings.models.description')}</label>
                        <span className="setting-desc">{t('agencySettings.models.descriptionHint')}</span>
                      </div>
                      <textarea
                        rows={4}
                        className="setting-value"
                        value={draft.description || ''}
                        placeholder={t('agencySettings.models.descriptionPlaceholder')}
                        onChange={(e) => updateModelDraft(model.id, { description: e.target.value })}
                      />
                    </div>

                    <div className="setting-row setting-row--compact agency-model-timezone-row">
                      <div className="setting-label-wrap">
                        <label className="setting-key">{t('agencySettings.models.timezone')}</label>
                        <span className="setting-desc">{t('agencySettings.models.timezoneHint')}</span>
                      </div>
                      <TimezoneSelect
                        value={draft.timezone || ''}
                        onChange={(value) => updateModelDraft(model.id, { timezone: value || '' })}
                        placeholder={t('agencySettings.models.timezonePlaceholder')}
                      />
                    </div>

                    <div className="setting-row setting-row--compact">
                      <div className="setting-label-wrap">
                        <label className="setting-key">{t('agencySettings.models.automationMode')}</label>
                        <span className="setting-desc">{t('agencySettings.models.automationModeHint')}</span>
                      </div>
                      <Select
                        value={draft.automation_mode}
                        onChange={(value) => updateModelDraft(model.id, { automation_mode: value || 'full_auto' })}
                        options={AUTOMATION_MODE_OPTIONS.map((mode) => ({ value: mode, label: automationLabel(t, mode) }))}
                        className="setting-select"
                      />
                    </div>

                    <div className="setting-row setting-row--compact">
                      <div className="setting-label-wrap">
                        <label className="setting-key">{t('agencySettings.models.defaultConfig')}</label>
                        <span className="setting-desc">{t('agencySettings.models.defaultConfigHint')}</span>
                      </div>
                      {canManageModelDefaultConfig ? (
                        <Select
                          value={draft.default_config_id}
                          onChange={(value) => updateModelDraft(model.id, { default_config_id: value || '' })}
                          options={graphConfigOptions}
                          className="setting-select"
                          dropdownAutoWidth
                        />
                      ) : (
                        <div className="form-group-value">{configDisplayName(configs, draft.default_config_id, t)}</div>
                      )}
                    </div>

                    <div className="agency-model-telegram-help">
                      <strong>{t('agencySettings.models.telegramSetupTitle')}</strong>
                      <button type="button" className="v2-btn v2-btn-secondary" onClick={() => {
                        setWorkspaceModelId(String(model.id))
                        setActiveSection('telegram')
                      }}>Open service-bot settings</button>
                    </div>
                  </div>

                  <div className="modal-actions agency-model-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={modelSavingId === model.id}
                      onClick={() => saveModelSettings(model, { bot_paused: !readBool(draft.bot_paused) })}
                    >
                      {readBool(draft.bot_paused) ? t('agencySettings.models.resumeBot') : t('agencySettings.models.pauseBot')}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={modelSavingId === model.id} onClick={() => saveModelSettings(model)}>
                      {modelSavingId === model.id ? t('common.saving') : t('agencySettings.models.save')}
                    </button>
                  </div>
                </section>
              )
            })}
            {models.length === 0 && <p className="muted">{t('agencySettings.models.empty')}</p>}
          </div>
        </ConfigBlock>
      ) : (
      <form onSubmit={handleSave} className="settings-form">
        <div className="agency-settings-config-strip">
          <div className="form-group">
            <label>{t('agencySettings.config.copy')}</label>
            <div className="form-group-value">{safeConfig?.name || (selectedConfigId ? `#${selectedConfigId}` : t('common.notSelected'))}</div>
          </div>
          {currentRole === 'agency' && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={copyingConfig}
              onClick={handleCopyAgencyConfig}
            >
              {copyingConfig ? t('common.saving') : t('agencySettings.config.copyActive')}
            </button>
          )}
          <p className="muted">{t('agencySettings.config.copyHint')}</p>
        </div>

        {activeSection === 'l4' && (
          <ConfigBlock title={t('agencySettings.l4.title')} hint={t('agencySettings.l4.hint')}>
            <VariableDefinitionsList
              ref={variableEditorRef}
              definitions={variableDefinitions}
              onSave={saveVariableDefinitions}
              saving={saving}
              level="l4"
              hidePanel
              compactOptionalName
            />
          </ConfigBlock>
        )}

        {activeSection === 'proactive' && (
          <>
            <ConfigBlock title={t('agencySettings.proactive.rulesTitle')} hint={t('agencySettings.proactive.rulesHint')}>
              {proactiveRules.length === 0 && <p className="muted">{t('settings.proactive.noRules')}</p>}
              {proactiveRules.map((rule, idx) => (
                <div className="variable-definition-block variable-level-l4" key={idx}>
                  <div className="variable-block-header">
                    <span className="variable-level-badge variable-level-badge--l4">{t('settings.proactive.ruleLabel', { n: idx + 1 })}</span>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setProactiveRules((prev) => prev.filter((_, i) => i !== idx))}>
                      {t('common.delete')}
                    </button>
                  </div>
                  <div className="proactive-rule-meta">
                    <label className="proactive-rule-field">
                      <span>{t('settings.proactive.ruleNameLabel')}</span>
                      <input
                        type="text"
                        value={rule.label || ''}
                        placeholder={t('settings.proactive.ruleNamePlaceholder')}
                        onChange={(e) => updateRule(idx, { label: e.target.value })}
                      />
                    </label>
                    <label className="proactive-rule-field proactive-rule-field--prompt">
                      <span>{t('settings.proactive.rulePromptLabel')}</span>
                      <textarea
                        value={rule.prompt || rule.prompt_text || ''}
                        placeholder={t('settings.proactive.rulePromptPlaceholder')}
                        rows={4}
                        onChange={(e) => updateRule(idx, { prompt: e.target.value })}
                      />
                    </label>
                  </div>
                  <ConditionBuilder
                    condition={rule.conditions}
                    onChange={(cond) => updateRule(idx, { conditions: cond })}
                    paramKeys={conditionParamKeys}
                    l4Keys={l4Keys.length > 0 ? l4Keys : null}
                    l4Definitions={variableDefinitions}
                    allowOr={false}
                    allowEmptyConditions
                    confirmRemove
                    fixedFirstRow={{
                      label: t('settings.proactive.hoursCondition'),
                      value: rule.minutes ?? DEFAULT_INTERVAL_MINUTES,
                      onChange: (value) => updateRule(idx, { minutes: value }),
                      unit: t('settings.proactive.minutesUnit') || 'min',
                      min: 1,
                      max: MAX_INTERVAL_MINUTES,
                      step: INTERVAL_STEP_MINUTES,
                    }}
                  />
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setProactiveRules((prev) => [...prev, emptyRule()])}>
                {t('settings.proactive.addRule')}
              </button>
            </ConfigBlock>

            <SettingsGrid title={t('agencySettings.proactive.cadenceTitle')}>
              {PROACTIVE_SETTING_KEYS.map((key) => (
                <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
              ))}
            </SettingsGrid>

            <SettingsGrid title={t('agencySettings.proactive.webhooksTitle')}>
              {WEBHOOK_SETTING_KEYS.map((key) => (
                <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
              ))}
            </SettingsGrid>
          </>
        )}

        {activeSection === 'prompt' && (
          <SettingsGrid title={t('agencySettings.promptControls.title')}>
            {PROMPT_CONTROL_KEYS.map((key) => (
              <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
            ))}
          </SettingsGrid>
        )}

        {activeSection === 'timewasters' && (
          <SettingsGrid title={t('agencySettings.timewasters.title')}>
            {TIMEWASTER_SETTING_KEYS.map((key) => (
              <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
            ))}
          </SettingsGrid>
        )}

        {activeSection === 'customPricing' && (
          <>
            <ConfigBlock
              title={t('agencySettings.customCapabilities.modelScopeTitle')}
              hint={t('agencySettings.customCapabilities.modelScopeHint')}
            >
              <div className="agency-custom-model-context">
                <label className="form-group">
                  <span>{t('agencySettings.customCapabilities.modelLabel')}</span>
                  <Select
                    value={selectedModelId}
                    onChange={handleCustomModelChange}
                    options={models.map((model) => {
                      const configId = model.default_config_id ?? activeGraphConfig?.id ?? ''
                      const config = configs.find((item) => String(item.id) === String(configId))
                      return {
                        value: String(model.id),
                        label: `${model.of_email || model.name || `#${model.id}`} — ${config?.name || (configId ? `Config #${configId}` : t('agencySettings.models.defaultConfigActive'))}`,
                      }
                    })}
                    className="setting-select"
                    dropdownAutoWidth
                  />
                </label>
                <div className="agency-custom-model-summary">
                  <span>{t('agencySettings.customCapabilities.editingModel')}</span>
                  <strong>{selectedCustomModel?.of_email || selectedCustomModel?.name || `#${selectedModelId}`}</strong>
                  <p>{t('agencySettings.customCapabilities.assignedConfig')}: <b>{selectedModelConfig?.name || safeConfig?.name || `Config #${selectedModelConfigId}`}</b>{selectedModelConfigId ? ` (#${selectedModelConfigId})` : ''}</p>
                  {selectedCustomModel?.default_config_id == null ? (
                    <p className="is-warning">{t('agencySettings.customCapabilities.globalConfigWarning')}</p>
                  ) : customConfigModels.length > 1 ? (
                    <p className="is-warning">{t('agencySettings.customCapabilities.sharedConfigWarning')}</p>
                  ) : (
                    <p className="is-safe">{t('agencySettings.customCapabilities.exclusiveConfig')}</p>
                  )}
                  <p className="muted">{t('agencySettings.customCapabilities.changeAssignmentHint')}</p>
                </div>
              </div>
            </ConfigBlock>
            <div className="settings-section-heading">
              <h2>{t('agencySettings.customCapabilities.title')}</h2>
            </div>
            {CUSTOM_CAPABILITY_GROUPS.map((group) => (
              <SettingsGrid key={group.id} title={t(`agencySettings.customCapabilities.group.${group.id}`)}>
                {group.keys.map((key) => (
                  <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
                ))}
              </SettingsGrid>
            ))}
            <SettingsGrid title={t('agencySettings.customPricing.title')}>
              {CUSTOM_PRICE_SETTING_KEYS.map((key) => (
                <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
              ))}
            </SettingsGrid>
            <CustomPricingPreview
              configId={selectedConfigId || null}
              settingsDraft={settingsDraft}
              t={t}
            />
          </>
        )}

        {activeSection === 'monetizationRoutes' && (
          <MonetizationRoutesSettings
            settingsDraft={settingsDraft}
            onChange={updateSetting}
            t={t}
          />
        )}

        {activeSection === 'conditionalPrompts' && canViewConditionalPrompts && (
          <ConfigBlock title={t('agencySettings.conditionalPrompts.title')} hint={t('agencySettings.conditionalPrompts.hint')}>
            <ConditionalPromptsEditor
              t={t}
              rulesByAgent={conditionalPrompts}
              onChange={setConditionalPrompts}
              conditionParamKeys={conditionParamKeys}
            />
          </ConfigBlock>
        )}

        {activeSection === 'reactivation' && (
          <SettingsGrid title={t('agencySettings.reactivation.settingsTitle')}>
            {HISTORY_SETTING_KEYS.map((key) => (
              <SettingField key={key} t={t} keyName={key} value={settingsDraft[key]} onChange={updateSetting} />
            ))}
          </SettingsGrid>
        )}

        {activeSection === 'transitions' && (
          <ConfigBlock title={t('agencySettings.config.transitions')} hint={t('agencySettings.config.transitionsHint')}>
            <div className="agency-transitions-list">
              {transitionsDraft.map((row) => {
                const key = String(row.key || '').trim()
                const hint = transitionHint(t, key)
                return (
                  <section className="agency-transition-row" key={row.id}>
                    <div className="agency-transition-header">
                      <div className="agency-transition-title">
                        <label className="setting-key" htmlFor={`transition-key-${row.id}`}>{t('agencySettings.transitions.key')}</label>
                        <input
                          id={`transition-key-${row.id}`}
                          type="text"
                          className="setting-value agency-transition-key-input"
                          value={row.key}
                          placeholder={t('agencySettings.transitions.keyPlaceholder')}
                          onChange={(e) => updateTransitionRow(row.id, { key: e.target.value })}
                        />
                      </div>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => removeTransitionRow(row.id)}>
                        {t('common.delete')}
                      </button>
                    </div>
                    {key && (
                      <div className="agency-transition-meta">
                        <h4>{transitionLabel(t, key)}</h4>
                        {hint && <p className="settings-hint">{hint}</p>}
                      </div>
                    )}
                    <ConditionBuilder
                      condition={row.condition}
                      onChange={(condition) => updateTransitionRow(row.id, { condition })}
                      paramKeys={conditionParamKeys}
                      l4Keys={l4Keys.length > 0 ? l4Keys : null}
                      l4Definitions={variableDefinitions}
                      confirmRemove
                      showEvalPreview={key === 'run_monetization'}
                      graphTransitions={transitionRowsToObject(transitionsDraft)}
                      showMonetizationGatePreview={key === 'run_monetization'}
                    />
                  </section>
                )
              })}
              {transitionsDraft.length === 0 && <p className="muted">{t('agencySettings.transitions.empty')}</p>}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addTransitionRow}>
                {t('agencySettings.transitions.add')}
              </button>
            </div>
          </ConfigBlock>
        )}

        {warnings.length > 0 && (
          <div className="settings-error">
            {warnings.map((w, idx) => <div key={idx}>{w.level}: {w.message}</div>)}
          </div>
        )}
        <div className="modal-actions agency-settings-savebar">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
      )}

      {activeSection === 'reactivation' && <ConfigBlock title={t('agencySettings.reactivation.queueTitle')} hint={t('agencySettings.reactivation.queueHint')}>
        <div className="settings-grid">
          <div className="form-group">
            <label>{t('agencySettings.models.model')}</label>
            <Select value={selectedModelId} onChange={handleCustomModelChange} options={modelOptions} placeholder={t('common.notSelected')} />
          </div>
          <div className="form-group">
            <label>{t('agencySettings.reactivation.status')}</label>
            <Select
              value={queueStatus}
              onChange={(value) => setQueueStatus(value || '')}
              options={QUEUE_STATUS_OPTIONS.map((value) => ({ value, label: value || t('agencySettings.reactivation.allStatuses') }))}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" disabled={!selectedModelId || queueLoading} onClick={() => refreshQueue()}>
            {t('common.refresh')}
          </button>
          <button type="button" className="btn" disabled={!selectedModelId || queueLoading} onClick={() => runHistoryAction(historyTick)}>
            {t('agencySettings.reactivation.runTick')}
          </button>
          <button type="button" className="btn btn-primary" disabled={!selectedModelId || queueLoading} onClick={() => runHistoryAction(historyDispatch)}>
            {t('agencySettings.reactivation.dispatchApproved')}
          </button>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('agencySettings.reactivation.fan')}</th>
                <th>{t('agencySettings.reactivation.queueStatus')}</th>
                <th>{t('agencySettings.reactivation.source')}</th>
                <th>{t('agencySettings.reactivation.lastMessage')}</th>
                <th>{t('agencySettings.reactivation.decision')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {queueRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.fan_external_id || row.fan_id}</td>
                  <td>{row.status}</td>
                  <td>{row.source || '—'}</td>
                  <td>{row.last_message_at || '—'}</td>
                  <td>{row.decision || row.reason || '—'}</td>
                  <td>
                    {row.status === 'candidate' && (
                      <button type="button" className="btn btn-sm" disabled={queueLoading} onClick={() => updateQueueItem(row.id, historyApprove)}>
                        {t('agencySettings.reactivation.approve')}
                      </button>
                    )}
                    {['candidate', 'approved', 'synced'].includes(row.status) && (
                      <button type="button" className="btn btn-sm btn-danger" disabled={queueLoading} onClick={() => updateQueueItem(row.id, historySkip)}>
                        {t('agencySettings.reactivation.skip')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {queueRows.length === 0 && (
                <tr>
                  <td colSpan={6}>{queueLoading ? t('common.loading') : t('agencySettings.reactivation.empty')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="muted">{t('agencySettings.reactivation.total', { count: queueTotal })}</p>
      </ConfigBlock>}
    </PageSection>
  )
}

function EngineApiPanel({ t, models, clients, credentials, onCreated, onRevoked, createClient, revokeClient, addToast }) {
  const [name, setName] = useState('Primary integration')
  const [callbackUrl, setCallbackUrl] = useState('')
  const [mode, setMode] = useState('shadow')
  const [selected, setSelected] = useState(() => models.map((model) => model.id))
  const [saving, setSaving] = useState(false)

  async function handleCreate(event) {
    event.preventDefault()
    setSaving(true)
    try {
      const created = await createClient({
        name: name.trim(),
        callback_url: callbackUrl.trim() || null,
        allowed_model_ids: selected,
        default_mode: mode,
        capabilities: {
          'history.read': true,
          'messages.send': true,
          'media.send': true,
          'offers.send': true,
          'purchases.read': true,
          'purchases.events': true,
          'typing.send': true,
          'handoff': true,
          'adult_content': true,
        },
      })
      onCreated(created)
      addToast(t('agencySettings.engineApi.created'), 'success')
    } catch (error) {
      addToast(getApiErrorMessage(error, t('common.error')), 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleRevoke(clientId) {
    try {
      await revokeClient(clientId)
      onRevoked(clientId)
      addToast(t('agencySettings.engineApi.revoked'), 'success')
    } catch (error) {
      addToast(getApiErrorMessage(error, t('common.error')), 'error')
    }
  }

  function toggleModel(modelId) {
    setSelected((current) => current.includes(modelId)
      ? current.filter((value) => value !== modelId)
      : [...current, modelId])
  }

  return (
    <ConfigBlock title={t('agencySettings.engineApi.title')} hint={t('agencySettings.engineApi.hint')}>
      {credentials && (
        <div className="settings-info agency-engine-credentials">
          <strong>{t('agencySettings.engineApi.credentialsOnce')}</strong>
          <label>
            <span>API key</span>
            <input readOnly value={credentials.api_key || ''} onFocus={(event) => event.target.select()} />
          </label>
          <label>
            <span>Signing secret</span>
            <input readOnly value={credentials.signing_secret || ''} onFocus={(event) => event.target.select()} />
          </label>
        </div>
      )}

      <form onSubmit={handleCreate} className="settings-form">
        <div className="settings-grid">
          <div className="form-group">
            <label>{t('agencySettings.engineApi.clientName')}</label>
            <input value={name} onChange={(event) => setName(event.target.value)} required maxLength={128} />
          </div>
          <div className="form-group">
            <label>{t('agencySettings.engineApi.callbackUrl')}</label>
            <input type="url" value={callbackUrl} onChange={(event) => setCallbackUrl(event.target.value)} placeholder="https://partner.example/webhooks/fanltv" />
          </div>
          <div className="form-group">
            <label>{t('agencySettings.engineApi.defaultMode')}</label>
            <Select
              value={mode}
              onChange={setMode}
              options={['shadow', 'copilot', 'autopilot', 'conversion_only', 'human_only'].map((value) => ({ value, label: value }))}
            />
          </div>
        </div>
        <div className="agency-model-event-list">
          {models.map((model) => (
            <label className="agency-model-event-option" key={model.id}>
              <input type="checkbox" checked={selected.includes(model.id)} onChange={() => toggleModel(model.id)} />
              <span>{model.of_email || model.name || `#${model.id}`}</span>
            </label>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" type="submit" disabled={saving || selected.length === 0}>
            {saving ? t('common.saving') : t('agencySettings.engineApi.create')}
          </button>
        </div>
      </form>

      <div className="data-table-wrap">
        <table className="data-table">
          <thead><tr><th>{t('agencySettings.engineApi.clientName')}</th><th>Key</th><th>{t('agencySettings.engineApi.defaultMode')}</th><th>{t('common.status')}</th><th>{t('common.actions')}</th></tr></thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>{client.name}</td>
                <td><code>flt_live_{client.key_prefix}_...</code></td>
                <td>{client.default_mode}</td>
                <td>{client.is_active ? t('users.active') : t('users.inactive')}</td>
                <td>{client.is_active && <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRevoke(client.id)}>{t('agencySettings.engineApi.revoke')}</button>}</td>
              </tr>
            ))}
            {clients.length === 0 && <tr><td colSpan={5}>{t('agencySettings.engineApi.empty')}</td></tr>}
          </tbody>
        </table>
      </div>
    </ConfigBlock>
  )
}

function ConditionalPromptsEditor({ t, rulesByAgent, onChange, conditionParamKeys }) {
  function updateAgentRules(agentKey, updater) {
    onChange((prev) => {
      const base = normalizeConditionalPrompts(prev || {})
      const current = Array.isArray(base[agentKey]) ? base[agentKey] : []
      const nextRules = typeof updater === 'function' ? updater(current) : updater
      return {
        ...base,
        [agentKey]: (nextRules || []).map(normalizeConditionalPromptRule),
      }
    })
  }

  function updateRule(agentKey, index, updates) {
    updateAgentRules(agentKey, (rules) =>
      rules.map((rule, ruleIndex) =>
        ruleIndex === index ? normalizeConditionalPromptRule({ ...rule, ...updates }, ruleIndex) : rule
      )
    )
  }

  return (
    <div className="agency-conditional-prompts">
      {CONDITIONAL_AGENT_KEYS.map((agentKey) => {
        const rules = Array.isArray(rulesByAgent?.[agentKey]) ? rulesByAgent[agentKey] : []
        return (
          <section className="agency-conditional-agent" key={agentKey}>
            <div className="agency-transition-meta">
              <h4>{t(`agencySettings.conditionalPrompts.agent.${agentKey}`)}</h4>
            </div>

            {rules.map((rule, index) => (
              <div className="variable-definition-block variable-level-l4 conditional-prompt-rule" key={`${agentKey}-${index}`}>
                <div className="variable-block-header">
                  <span className="variable-level-badge variable-level-badge--l4">
                    {t('settings.proactive.ruleLabel', { n: index + 1 })}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => updateAgentRules(agentKey, (agentRules) => agentRules.filter((_, ruleIndex) => ruleIndex !== index))}
                  >
                    {t('common.delete')}
                  </button>
                </div>

                <ConditionBuilder
                  condition={rule.condition}
                  onChange={(condition) => updateRule(agentKey, index, { condition })}
                  paramKeys={conditionParamKeys}
                />

                <div className="settings-grid">
                  <div className="setting-row setting-row--compact">
                    <div className="setting-label-wrap">
                      <label className="setting-key">{t('agencySettings.conditionalPrompts.enabled')}</label>
                    </div>
                    <Switch
                      checked={rule.enabled !== false}
                      onChange={(enabled) => updateRule(agentKey, index, { enabled })}
                      ariaLabel={t('agencySettings.conditionalPrompts.enabled')}
                      className="setting-switch"
                    />
                  </div>
                  <div className="setting-row setting-row--compact">
                    <div className="setting-label-wrap">
                      <label className="setting-key">{t('agencySettings.conditionalPrompts.role')}</label>
                    </div>
                    <Select
                      value={rule.role || 'system'}
                      onChange={(role) => updateRule(agentKey, index, { role: role || 'system' })}
                      options={['system', 'user'].map((role) => ({ value: role, label: role }))}
                      className="setting-select"
                    />
                  </div>
                  <div className="setting-row setting-row--compact">
                    <div className="setting-label-wrap">
                      <label className="setting-key">{t('agencySettings.conditionalPrompts.order')}</label>
                    </div>
                    <input
                      type="number"
                      className="setting-value"
                      value={String(rule.order ?? index)}
                      onChange={(event) => updateRule(agentKey, index, { order: Number(event.target.value) })}
                    />
                  </div>
                </div>

                <div className="setting-row">
                  <div className="setting-label-wrap">
                    <label className="setting-key">{t('agencySettings.conditionalPrompts.prompt')}</label>
                  </div>
                  <textarea
                    rows={4}
                    className="setting-value"
                    value={rule.prompt_text || ''}
                    placeholder={t('settings.conditionalPrompts.promptPlaceholder')}
                    onChange={(event) => updateRule(agentKey, index, { prompt_text: event.target.value })}
                  />
                </div>
              </div>
            ))}

            {rules.length === 0 && <p className="muted">{t('agencySettings.conditionalPrompts.empty')}</p>}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => updateAgentRules(agentKey, (agentRules) => [...agentRules, emptyConditionalPromptRule(agentRules.length)])}
            >
              {t('agencySettings.conditionalPrompts.addRule')}
            </button>
          </section>
        )
      })}
    </div>
  )
}

function ConfigBlock({ title, hint, children }) {
  return (
    <div className="settings-section-block">
      <div className="settings-section-heading">
        <h3>{title}</h3>
        {hint && <p className="settings-hint">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

function SettingsGrid({ title, children }) {
  return (
    <ConfigBlock title={title}>
      <div className="settings-grid">{children}</div>
    </ConfigBlock>
  )
}

function SettingField({ t, keyName, value, onChange }) {
  const label = t(`agencySettings.setting.${keyName}`) || keyName
  const desc = t(`agencySettings.settingDesc.${keyName}`)
  const hasDesc = desc && desc !== `agencySettings.settingDesc.${keyName}`
  const rowClassName = `setting-row${hasDesc ? '' : ' setting-row--compact'}`
  if (BOOLEAN_SETTING_KEYS.has(keyName)) {
    return (
      <div className={rowClassName}>
        <div className="setting-label-wrap">
          <label className="setting-key">{label}</label>
          {hasDesc && <span className="setting-desc">{desc}</span>}
        </div>
        <Switch checked={readBool(value)} onChange={(checked) => onChange(keyName, checked)} ariaLabel={label} className="setting-switch" />
      </div>
    )
  }
  if (keyName === 'prompt_emoji_level') {
    return (
      <SelectSetting
        t={t}
        keyName={keyName}
        label={label}
        desc={hasDesc ? desc : ''}
        value={value || 'normal'}
        onChange={onChange}
        options={['none', 'light', 'normal', 'high']}
      />
    )
  }
  if (keyName === 'prompt_message_length') {
    return (
      <SelectSetting
        t={t}
        keyName={keyName}
        label={label}
        desc={hasDesc ? desc : ''}
        value={value || 'auto'}
        onChange={onChange}
        options={['auto', 'short', 'medium', 'long']}
      />
    )
  }
  if (keyName === 'custom_pricing_currency') {
    return (
      <SelectSetting
        t={t}
        keyName={keyName}
        label={label}
        desc={hasDesc ? desc : ''}
        value={value || 'USD'}
        onChange={onChange}
        options={['USD', 'EUR', 'GBP']}
      />
    )
  }
  if (keyName === 'custom_max_nudity_level') {
    return (
      <div className={rowClassName}>
        <div className="setting-label-wrap">
          <label className="setting-key">{label}</label>
          {hasDesc && <span className="setting-desc">{desc}</span>}
        </div>
        <Select
          options={CUSTOM_NUDITY_OPTIONS.map(([optionValue, fallback]) => ({
            value: optionValue,
            label: t(`agencySettings.customCapabilities.nudity.${optionValue}`) || fallback,
          }))}
          value={value || 'clothed'}
          onChange={(next) => onChange(keyName, next)}
          className="setting-select"
        />
      </div>
    )
  }
  if (CHECKLIST_SETTING_KEYS.has(keyName)) {
    const selected = new Set(Array.isArray(value) ? value : [])
    return (
      <div className={`${rowClassName} setting-row--checklist`}>
        <div className="setting-label-wrap">
          <label className="setting-key">{label}</label>
          {hasDesc && <span className="setting-desc">{desc}</span>}
        </div>
        <div className="custom-capability-checklist">
          {CUSTOM_PROP_OPTIONS.map(([optionValue, fallback]) => (
            <label key={optionValue}>
              <input
                type="checkbox"
                checked={selected.has(optionValue)}
                onChange={(event) => {
                  const next = new Set(selected)
                  if (event.target.checked) next.add(optionValue)
                  else next.delete(optionValue)
                  onChange(keyName, Array.from(next))
                }}
              />
              <span>{t(`agencySettings.customCapabilities.prop.${optionValue}`) || fallback}</span>
            </label>
          ))}
        </div>
      </div>
    )
  }
  const isTextarea = keyName === 'prompt_custom_safe_instructions' || LIST_SETTING_KEYS.has(keyName)
  const isDeposit = keyName === 'custom_pricing_minimum_deposit_percent'
  const isCustomPercent = keyName.startsWith('custom_pricing_') && keyName.endsWith('_percent')
  const isCustomMoney = keyName.startsWith('custom_pricing_') && !isCustomPercent
  return (
    <div className={rowClassName}>
      <div className="setting-label-wrap">
        <label className="setting-key">{label}</label>
        {hasDesc && <span className="setting-desc">{desc}</span>}
      </div>
      {isTextarea ? (
        <textarea
          rows={keyName === 'prompt_custom_safe_instructions' ? 4 : 2}
          value={String(value ?? '')}
          onChange={(e) => onChange(keyName, e.target.value)}
          className="setting-value"
        />
      ) : (
        <input
          type={String(keyName).startsWith('prompt_') ? 'text' : 'number'}
          min={isDeposit ? 50 : (isCustomMoney || isCustomPercent ? 0 : undefined)}
          max={isDeposit ? 100 : (isCustomMoney || isCustomPercent ? (isCustomMoney ? 100000 : 500) : undefined)}
          step={isCustomMoney ? 0.01 : 1}
          value={String(value ?? '')}
          onChange={(e) => onChange(keyName, e.target.value)}
          className="setting-value"
        />
      )}
    </div>
  )
}

function SelectSetting({ keyName, label, desc, value, onChange, options }) {
  return (
    <div className={`setting-row${desc ? '' : ' setting-row--compact'}`}>
      <div className="setting-label-wrap">
        <label className="setting-key">{label}</label>
        {desc && <span className="setting-desc">{desc}</span>}
      </div>
      <Select
        options={options.map((item) => ({ value: item, label: item }))}
        value={value}
        onChange={(next) => onChange(keyName, next)}
        className="setting-select"
      />
    </div>
  )
}
